// Theme controller — light / dark / system tri-state, overrides OS preference.
//
// Pre-paint apply is in public/js/theme-boot.js (head sync, no flash). This module
// owns runtime get/set, aria-checked sync on the user-menu radios, window.Theme,
// and the theme:changed event the user-menu listens to (auto-close on change).
//
// localStorage may throw (SecurityError) in private / sandboxed contexts; every
// read/write is guarded. setTheme applies the DOM first so the choice always takes
// effect even when persistence fails (the choice then holds for the session only).

const STORAGE_KEY = "theme";
const DEFAULT: Theme = "system";

export type Theme = "system" | "light" | "dark";

function isTheme(v: string | null): v is Theme {
  return v === "system" || v === "light" || v === "dark";
}

export function getTheme(): Theme {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (isTheme(saved)) return saved;
  } catch {
    /* storage blocked (private / sandboxed) → default */
  }
  return DEFAULT;
}

export function applyTheme(theme: Theme): void {
  document.documentElement.dataset.theme = theme;
  document.querySelectorAll<HTMLElement>(".theme-btn").forEach((btn) => {
    btn.setAttribute("aria-checked", btn.dataset["themeChoice"] === theme ? "true" : "false");
  });
}

export function setTheme(theme: Theme): void {
  // Apply first → DOM always reflects the choice even if persistence throws below.
  applyTheme(theme);
  window.dispatchEvent(new CustomEvent("theme:changed", { detail: { theme } }));
  try {
    localStorage.setItem(STORAGE_KEY, theme);
  } catch {
    /* storage blocked → choice holds for this session only */
  }
}

document.addEventListener("DOMContentLoaded", () => {
  // theme-boot already set data-theme pre-paint; this re-asserts + syncs the radios.
  applyTheme(getTheme());
});

window.Theme = {
  get: getTheme,
  set: (theme: string) => setTheme(isTheme(theme) ? theme : DEFAULT),
};
